import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { invalidatePropertyManagersCache, PropertyManagersRepository } from '~/repositories/propertyManagersRepository';
import type { PropertyManagerDto, UpdatePropertyManagerDto } from '~/swagger/portal/Api';

const repo = new PropertyManagersRepository();

export const [PropertyManagersProvider, usePropertyManagers] = createMagicDoorContext('PropertyManagers', () => {
  const [currentManager, setCurrentManager] = createSignal<PropertyManagerDto | undefined>(undefined);
  const [propertyManagers, { fetch, refetch }] = createLazyResource(async () => {
    setCurrentManager(undefined);
    const data = await repo.getPropertyManagers();
    return data;
  });

  const updatePropertyManager = createMutation(async (id: string, model: UpdatePropertyManagerDto) => {
    await repo.updatePropertyManager(id, model);
    invalidatePropertyManagersCache();
  });

  const addPropertyManager = createMutation(async (model: PropertyManagerDto) => {
    await repo.createPropertyManager(model);
    invalidatePropertyManagersCache();
  });

  const deletePropertyManager = createMutation(async (id: string) => {
    await repo.deletePropertyManager(id);
    invalidatePropertyManagersCache();
  });

  const getPropertyManagerInfo = createMutation(async (id: string) => {
    return await repo.getPropertyManagerById(id);
  });

  const changePropertyManagerPassword = createMutation(async (id: string, currentPassword: string, newPassword: string) => {
    await repo.changePropertyManagerPassword(id, currentPassword, newPassword);
  });

  const changePropertyManagerActiveStatus = createMutation(async (id: string, isActive: boolean) => {
    await repo.changePropertyManagerActiveStatus(id, isActive);
    invalidatePropertyManagersCache();
  });

  return {
    get propertyManagers() {
      fetch();
      return propertyManagers;
    },
    get loading() {
      fetch();
      return () => propertyManagers.loading;
    },
    updatePropertyManager,
    refetch,
    currentManager,
    setCurrentManager,
    addPropertyManager,
    deletePropertyManager,
    getPropertyManagerInfo,
    changePropertyManagerPassword,
    changePropertyManagerActiveStatus,
  };
});
